/**
 * InterviewManagementPage - 면담관리 페이지
 *
 * V55.67: 면담 타입/템플릿 CRUD 관리
 *
 * 기능:
 * - 탭1: 면담 타입 관리 (8가지 기본 타입 자동 시딩)
 * - 탭2: 면담 템플릿 관리 (타입별 본문 템플릿)
 *
 * 권한:
 * - admin-like (system_admin, role_admin_kr, role_admin_cn)
 * - role_manager
 * - role_exec
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  FileText,
  Loader,
  AlertCircle,
  RefreshCw,
  Building2,
} from 'lucide-react';
import {
  useInterviewTypes,
  useInterviewTemplates,
  InterviewType,
  InterviewTemplate,
  useOrgInterviewTemplateSelections,
} from '../../shared/hooks/useInterviewTypesManagement';
import { useAuthContext } from '../../shared/hooks/useAuthContext';
import { ModalOverlay } from '../../shared/components/ModalOverlay';

// ============================================
// 타입 정의
// ============================================

type TabType = 'types' | 'templates';

// ============================================
// 메인 컴포넌트
// ============================================

export function InterviewManagementPage() {
  const navigate = useNavigate();
  const authContext = useAuthContext();
  const [activeTab, setActiveTab] = useState<TabType>('types');

  // V55.67.5: system_admin 감지 SSoT (auth_roleAssignments 기반)
  // user.roles는 SSoT가 아님. roleAssignments에서 직접 확인해야 함.
  const isSystemAdmin = authContext.roleAssignments.some(
    (ra) => ra.roleId === 'system_admin' && ra.isActive
  );
  const [selectedCompanyCode, setSelectedCompanyCode] = useState<string>('KR');

  // V55.67.5: authContext 로딩 대기 (타이밍 이슈 방지)
  // isLoaded가 false면 roleAssignments가 빈 배열이므로 isSystemAdmin이 false로 잘못 판정됨
  if (!authContext.isLoaded) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '8px', color: 'var(--color-gray-500)' }}>권한 확인 중...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* 헤더 */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px',
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              padding: 0,
              border: 'none',
              borderRadius: '6px',
              background: 'var(--color-gray-100)',
              cursor: 'pointer',
            }}
            aria-label="뒤로가기"
          >
            <ArrowLeft size={18} style={{ color: 'var(--color-gray-600)' }} />
          </button>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
            면담관리
          </h1>

          {/* V55.67.3: system_admin 전용 회사 선택 드롭다운 */}
          {isSystemAdmin && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginLeft: 'auto',
            }}>
              <Building2 size={16} style={{ color: 'var(--color-gray-500)' }} />
              <select
                value={selectedCompanyCode}
                onChange={(e) => setSelectedCompanyCode(e.target.value)}
                style={{
                  padding: '6px 12px',
                  fontSize: '14px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '6px',
                  background: 'white',
                  cursor: 'pointer',
                }}
              >
                <option value="KR">한국 (KR)</option>
                <option value="CN">중국 (CN)</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* 탭 */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid var(--color-gray-200)',
        marginBottom: '24px',
      }}>
        <TabButton
          label="면담 타입"
          isActive={activeTab === 'types'}
          onClick={() => setActiveTab('types')}
        />
        <TabButton
          label="면담 템플릿"
          isActive={activeTab === 'templates'}
          onClick={() => setActiveTab('templates')}
        />
      </div>

      {/* 콘텐츠 */}
      <div>
        {activeTab === 'types' && (
          <InterviewTypesTab
            companyCode={isSystemAdmin ? selectedCompanyCode : undefined}
            isSystemAdmin={isSystemAdmin}
          />
        )}
        {activeTab === 'templates' && (
          <InterviewTemplatesTab
            companyCode={isSystemAdmin ? selectedCompanyCode : undefined}
            isSystemAdmin={isSystemAdmin}
          />
        )}
      </div>
    </div>
  );
}

// ============================================
// 탭 버튼 컴포넌트
// ============================================

interface TabButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function TabButton({ label, isActive, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '12px 24px',
        border: 'none',
        background: 'transparent',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
        color: isActive ? 'var(--color-blue-600)' : 'var(--color-gray-500)',
        borderBottom: isActive ? '2px solid var(--color-blue-600)' : '2px solid transparent',
        marginBottom: '-1px',
      }}
    >
      {label}
    </button>
  );
}

// ============================================
// 면담 타입 탭
// ============================================

interface InterviewTypesTabProps {
  companyCode?: string; // V55.67.3: system_admin용
  isSystemAdmin: boolean; // V55.67.7: 가드용
}

function InterviewTypesTab({ companyCode, isSystemAdmin }: InterviewTypesTabProps) {
  // V55.67.7: system_admin은 companyCode 필수, 그 외는 undefined 허용 (BE에서 해결)
  const hookEnabled = isSystemAdmin ? !!companyCode : true;

  const {
    types,
    isLoading,
    error,
    refetch,
    createType,
    updateType,
    deleteType,
    isCreating,
    isUpdating,
    isDeleting,
  } = useInterviewTypes({ companyCode, includeInactive: true, enabled: hookEnabled });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingType, setEditingType] = useState<InterviewType | null>(null);

  const handleToggleActive = useCallback(
    async (type: InterviewType) => {
      try {
        await updateType(type.id, { isActive: !type.isActive });
      } catch (err) {
        console.error('타입 활성화/비활성화 실패:', err);
        alert('작업에 실패했습니다.');
      }
    },
    [updateType]
  );

  const handleDeleteType = useCallback(
    async (type: InterviewType) => {
      const confirmed = window.confirm(
        `"${type.name}" 면담타입을 삭제하시겠습니까?\n\n연관된 템플릿도 함께 삭제됩니다.`
      );
      if (!confirmed) return;

      try {
        await deleteType(type.id);
        alert('면담타입이 삭제되었습니다.');
      } catch (err: any) {
        console.error('면담타입 삭제 실패:', err);
        alert(err?.message || '면담타입 삭제에 실패했습니다.');
      }
    },
    [deleteType]
  );

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 0',
        color: 'var(--color-gray-500)',
      }}>
        <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '8px' }}>로딩 중...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 0',
        gap: '16px',
      }}>
        <AlertCircle size={32} style={{ color: 'var(--color-red-500)' }} />
        <p style={{ color: 'var(--color-gray-600)', margin: 0 }}>{error.message}</p>
        <button
          onClick={refetch}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            fontSize: '13px',
            color: 'var(--color-blue-600)',
            background: 'var(--color-blue-50)',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={14} />
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* 타입 추가 버튼 */}
      <button
        onClick={() => setShowCreateModal(true)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '14px',
          marginBottom: '16px',
          background: 'white',
          border: '1px dashed var(--color-gray-300)',
          borderRadius: '8px',
          color: 'var(--color-gray-600)',
          fontSize: '14px',
          cursor: 'pointer',
        }}
      >
        <Plus size={16} />
        타입 추가
      </button>

      {/* 타입 목록 */}
      <div style={{
        background: 'white',
        border: '1px solid var(--color-gray-200)',
        borderRadius: '8px',
        overflow: 'hidden',
      }}>
        {types.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: 'var(--color-gray-500)',
          }}>
            면담 타입이 없습니다.
          </div>
        ) : (
          types.map((type, index) => (
            <div
              key={type.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 16px',
                borderTop: index > 0 ? '1px solid var(--color-gray-100)' : 'none',
                background: !type.isActive ? 'var(--color-gray-50)' : 'white',
                opacity: !type.isActive ? 0.6 : 1,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 500, color: 'var(--color-gray-900)' }}>
                    {type.name}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    color: 'var(--color-gray-400)',
                    background: 'var(--color-gray-100)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                  }}>
                    {type.code}
                  </span>
                  {!type.isActive && (
                    <span style={{
                      fontSize: '11px',
                      color: 'var(--color-red-500)',
                      background: 'var(--color-red-50)',
                      padding: '2px 6px',
                      borderRadius: '4px',
                    }}>
                      비활성
                    </span>
                  )}
                </div>
                {type.description && (
                  <p style={{
                    margin: '4px 0 0 0',
                    fontSize: '13px',
                    color: 'var(--color-gray-500)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {type.description}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => setEditingType(type)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    padding: 0,
                    border: 'none',
                    borderRadius: '4px',
                    background: 'transparent',
                    color: 'var(--color-gray-500)',
                    cursor: 'pointer',
                  }}
                  title="수정"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDeleteType(type)}
                  disabled={isDeleting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    padding: 0,
                    border: 'none',
                    borderRadius: '4px',
                    background: 'transparent',
                    color: 'var(--color-red-400)',
                    cursor: isDeleting ? 'not-allowed' : 'pointer',
                  }}
                  title="삭제"
                >
                  <Trash2 size={16} />
                </button>
                <button
                  onClick={() => handleToggleActive(type)}
                  disabled={isUpdating}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    padding: 0,
                    border: 'none',
                    borderRadius: '4px',
                    background: 'transparent',
                    color: type.isActive ? 'var(--color-green-600)' : 'var(--color-gray-400)',
                    cursor: 'pointer',
                  }}
                  title={type.isActive ? '비활성화' : '활성화'}
                >
                  {type.isActive ? (
                    <ToggleRight size={20} />
                  ) : (
                    <ToggleLeft size={20} />
                  )}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* 타입 생성/수정 모달 */}
      {showCreateModal && (
        <InterviewTypeModal
          onClose={() => setShowCreateModal(false)}
          onSave={async (data) => {
            if ('code' in data && data.code) {
              await createType({ code: data.code, name: data.name, description: data.description });
            }
            setShowCreateModal(false);
          }}
          isSaving={isCreating}
        />
      )}
      {editingType && (
        <InterviewTypeModal
          type={editingType}
          onClose={() => setEditingType(null)}
          onSave={async (data) => {
            await updateType(editingType.id, { name: data.name, description: data.description });
            setEditingType(null);
          }}
          isSaving={isUpdating}
        />
      )}
    </div>
  );
}

// ============================================
// 면담 타입 모달
// ============================================

interface InterviewTypeModalProps {
  type?: InterviewType;
  onClose: () => void;
  onSave: (data: { code?: string; name: string; description?: string }) => Promise<void>;
  isSaving: boolean;
}

function InterviewTypeModal({ type, onClose, onSave, isSaving }: InterviewTypeModalProps) {
  const [code, setCode] = useState(type?.code || '');
  const [name, setName] = useState(type?.name || '');
  const [description, setDescription] = useState(type?.description || '');

  const isEdit = !!type;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      alert('타입명을 입력해주세요.');
      return;
    }

    if (!isEdit && !code.trim()) {
      alert('타입 코드를 입력해주세요.');
      return;
    }

    try {
      await onSave({
        ...(isEdit ? {} : { code: code.trim() }),
        name: name.trim(),
        description: description.trim() || undefined,
      });
    } catch (err: any) {
      alert(err.message || '저장에 실패했습니다.');
    }
  };

  return (
    <>
      {/* Backdrop */}
      <ModalOverlay
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 998,
        }}
        onClose={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          width: '100%',
          maxWidth: '440px',
          zIndex: 999,
        }}
      >
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--color-gray-200)',
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
            {isEdit ? '면담 타입 수정' : '면담 타입 추가'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {!isEdit && (
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>
                타입 코드 <span style={{ color: 'var(--color-red-500)' }}>*</span>
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toLowerCase().replace(/\s/g, '_'))}
                placeholder="예: custom_interview"
                style={inputStyle}
                disabled={isSaving}
              />
              <p style={{
                margin: '6px 0 0 0',
                fontSize: '12px',
                color: 'var(--color-gray-500)',
              }}>
                영문 소문자와 밑줄(_)만 사용 가능합니다.
              </p>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>
              타입명 <span style={{ color: 'var(--color-red-500)' }}>*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 커스텀 면담"
              style={inputStyle}
              disabled={isSaving}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle}>설명</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="타입에 대한 설명을 입력하세요."
              rows={3}
              style={{
                ...inputStyle,
                resize: 'none',
              }}
              disabled={isSaving}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--color-gray-700)',
                background: 'var(--color-gray-100)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
              disabled={isSaving}
            >
              취소
            </button>
            <button
              type="submit"
              style={{
                flex: 1,
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: 500,
                color: 'white',
                background: 'var(--color-blue-600)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                opacity: isSaving ? 0.5 : 1,
              }}
              disabled={isSaving}
            >
              {isSaving ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  저장 중...
                </span>
              ) : (
                '저장'
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// ============================================
// 면담 템플릿 탭
// ============================================

interface InterviewTemplatesTabProps {
  companyCode?: string; // V55.67.3: system_admin용
  isSystemAdmin: boolean; // V55.67.7: 가드용
}

function InterviewTemplatesTab({ companyCode, isSystemAdmin }: InterviewTemplatesTabProps) {
  // V55.67.7: system_admin은 companyCode 필수, 그 외는 undefined 허용 (BE에서 해결)
  const hookEnabled = isSystemAdmin ? !!companyCode : true;

  // V268: 조직별 대표설정을 위해 현재 사용자의 orgUnitId 조회
  const authContext = useAuthContext();
  const userOrgUnitId = authContext.orgUnitId;

  const { types, isLoading: typesLoading, refetch: refetchTypes } = useInterviewTypes({ companyCode, includeInactive: true, enabled: hookEnabled });
  const [selectedTypeId, setSelectedTypeId] = useState<string | undefined>(undefined);

  const {
    templates,
    isLoading,
    error,
    refetch,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    isCreating,
    isUpdating,
    isDeleting,
  } = useInterviewTemplates({ companyCode, typeId: selectedTypeId, includeInactive: true, enabled: hookEnabled });

  // V268: 조직별 대표 템플릿 선택 (orgInterviewTemplates 기반)
  const {
    selections: orgSelections,
    refetch: refetchOrgSelections,
    setSelection: setOrgSelection,
    isSetting: isSettingDefault,
  } = useOrgInterviewTemplateSelections(userOrgUnitId, companyCode);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<InterviewTemplate | null>(null);

  const handleToggleActive = useCallback(
    async (template: InterviewTemplate) => {
      try {
        await updateTemplate(template.id, { isActive: !template.isActive });
      } catch (err) {
        console.error('템플릿 활성화/비활성화 실패:', err);
        alert('작업에 실패했습니다.');
      }
    },
    [updateTemplate]
  );

  const handleDeleteTemplate = useCallback(
    async (template: InterviewTemplate) => {
      if (!window.confirm(`면담 템플릿 "${template.name}"을(를) 삭제하시겠습니까?\n삭제 후 복구할 수 없습니다.`)) {
        return;
      }
      try {
        await deleteTemplate(template.id);
        alert('템플릿이 삭제되었습니다.');
      } catch (err) {
        console.error('템플릿 삭제 실패:', err);
        alert('템플릿 삭제에 실패했습니다.');
      }
    },
    [deleteTemplate]
  );

  // V268: 대표 템플릿 설정 (조직별)
  // 기존(V284): interviewTypes.defaultTemplateId (전역) → 전 조직 공통 적용 버그
  // 수정: orgInterviewTemplates (orgUnitId + typeCode) → 조직별 독립 적용
  const handleSetDefault = useCallback(
    async (template: InterviewTemplate) => {
      if (!userOrgUnitId) {
        alert('소속 조직 정보가 없습니다.');
        return;
      }
      // typeId → typeCode 변환 (orgInterviewTemplates는 typeCode 사용)
      const parentType = types.find((t) => t.id === template.typeId);
      if (!parentType) {
        alert('면담 타입 정보를 찾을 수 없습니다.');
        return;
      }
      try {
        await setOrgSelection(parentType.code, template.id);
        await refetchOrgSelections();
        alert(`"${template.name}"이(가) 대표 템플릿으로 설정되었습니다.`);
      } catch (err) {
        console.error('대표 템플릿 설정 실패:', err);
        alert('대표 템플릿 설정에 실패했습니다.');
      }
    },
    [userOrgUnitId, types, setOrgSelection, refetchOrgSelections]
  );

  if (typesLoading || isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 0',
        color: 'var(--color-gray-500)',
      }}>
        <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '8px' }}>로딩 중...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 0',
        gap: '16px',
      }}>
        <AlertCircle size={32} style={{ color: 'var(--color-red-500)' }} />
        <p style={{ color: 'var(--color-gray-600)', margin: 0 }}>{error.message}</p>
        <button
          onClick={refetch}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            fontSize: '13px',
            color: 'var(--color-blue-600)',
            background: 'var(--color-blue-50)',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          <RefreshCw size={14} />
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* 타입 선택 */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>면담 타입 선택</label>
        <select
          value={selectedTypeId || ''}
          onChange={(e) => setSelectedTypeId(e.target.value || undefined)}
          style={inputStyle}
        >
          <option value="">전체 템플릿</option>
          {types.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name} {!type.isActive && '(비활성)'}
            </option>
          ))}
        </select>
      </div>

      {/* 템플릿 추가 버튼 */}
      <button
        onClick={() => setShowCreateModal(true)}
        disabled={types.length === 0}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '14px',
          marginBottom: '16px',
          background: 'white',
          border: '1px dashed var(--color-gray-300)',
          borderRadius: '8px',
          color: 'var(--color-gray-600)',
          fontSize: '14px',
          cursor: types.length === 0 ? 'not-allowed' : 'pointer',
          opacity: types.length === 0 ? 0.5 : 1,
        }}
      >
        <Plus size={16} />
        템플릿 추가
      </button>

      {/* 템플릿 목록 */}
      <div style={{
        background: 'white',
        border: '1px solid var(--color-gray-200)',
        borderRadius: '8px',
        overflow: 'hidden',
      }}>
        {templates.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: 'var(--color-gray-500)',
          }}>
            {selectedTypeId
              ? '해당 타입의 템플릿이 없습니다.'
              : '면담 템플릿이 없습니다.'}
          </div>
        ) : (
          templates.map((template, index) => {
            const parentType = types.find((t) => t.id === template.typeId);
            // V268: 조직별 대표 판정 (orgInterviewTemplates 기반)
            // 기존(V284): parentType?.defaultTemplateId === template.id (전역)
            const isDefault = parentType
              ? orgSelections.some((s) => s.typeCode === parentType.code && s.templateId === template.id)
              : false;
            return (
              <div
                key={template.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 16px',
                  borderTop: index > 0 ? '1px solid var(--color-gray-100)' : 'none',
                  background: !template.isActive ? 'var(--color-gray-50)' : isDefault ? '#FFFDE7' : 'white',
                  opacity: !template.isActive ? 0.6 : 1,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileText size={16} style={{ color: 'var(--color-gray-400)' }} />
                    <span style={{ fontWeight: 500, color: 'var(--color-gray-900)' }}>
                      {template.name}
                    </span>
                    {isDefault && (
                      <span style={{
                        fontSize: '11px',
                        color: '#F57F17',
                        background: '#FFF9C4',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: 600,
                      }}>
                        대표
                      </span>
                    )}
                    {!template.isActive && (
                      <span style={{
                        fontSize: '11px',
                        color: 'var(--color-red-500)',
                        background: 'var(--color-red-50)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                      }}>
                        비활성
                      </span>
                    )}
                  </div>
                  {parentType && (
                    <p style={{
                      margin: '4px 0 0 0',
                      fontSize: '13px',
                      color: 'var(--color-gray-500)',
                    }}>
                      {parentType.name}
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* V284: 대표 템플릿 설정 버튼 */}
                  {!isDefault && template.isActive && (
                    <button
                      onClick={() => handleSetDefault(template)}
                      disabled={isSettingDefault}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#F57F17',
                        background: '#FFF9C4',
                        border: '1px solid #FFF176',
                        borderRadius: '4px',
                        cursor: isSettingDefault ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                      title="대표 템플릿으로 설정"
                    >
                      {isSettingDefault ? '설정 중...' : '대표 설정'}
                    </button>
                  )}
                  <button
                    onClick={() => setEditingTemplate(template)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '28px',
                      padding: 0,
                      border: 'none',
                      borderRadius: '4px',
                      background: 'transparent',
                      color: 'var(--color-gray-500)',
                      cursor: 'pointer',
                    }}
                    title="수정"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleToggleActive(template)}
                    disabled={isUpdating}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '28px',
                      padding: 0,
                      border: 'none',
                      borderRadius: '4px',
                      background: 'transparent',
                      color: template.isActive ? 'var(--color-green-600)' : 'var(--color-gray-400)',
                      cursor: 'pointer',
                    }}
                    title={template.isActive ? '비활성화' : '활성화'}
                  >
                    {template.isActive ? (
                      <ToggleRight size={20} />
                    ) : (
                      <ToggleLeft size={20} />
                    )}
                  </button>
                  <button
                    onClick={() => handleDeleteTemplate(template)}
                    disabled={isDeleting}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '28px',
                      height: '28px',
                      padding: 0,
                      border: 'none',
                      borderRadius: '4px',
                      background: 'transparent',
                      color: 'var(--color-red-500)',
                      cursor: 'pointer',
                    }}
                    title="삭제"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 템플릿 생성/수정 모달 */}
      {showCreateModal && (
        <InterviewTemplateModal
          types={types}
          onClose={() => setShowCreateModal(false)}
          onSave={async (data) => {
            if ('typeId' in data && data.typeId) {
              await createTemplate({ typeId: data.typeId, name: data.name, contentHtml: data.contentHtml });
            }
            setShowCreateModal(false);
          }}
          isSaving={isCreating}
        />
      )}
      {editingTemplate && (
        <InterviewTemplateModal
          template={editingTemplate}
          types={types}
          onClose={() => setEditingTemplate(null)}
          onSave={async (data) => {
            await updateTemplate(editingTemplate.id, { name: data.name, contentHtml: data.contentHtml });
            setEditingTemplate(null);
          }}
          isSaving={isUpdating}
        />
      )}
    </div>
  );
}

// ============================================
// 면담 템플릿 모달
// ============================================

interface InterviewTemplateModalProps {
  template?: InterviewTemplate;
  types: InterviewType[];
  onClose: () => void;
  onSave: (data: { typeId?: string; name: string; contentHtml: string }) => Promise<void>;
  isSaving: boolean;
}

function InterviewTemplateModal({
  template,
  types,
  onClose,
  onSave,
  isSaving,
}: InterviewTemplateModalProps) {
  const [typeId, setTypeId] = useState(template?.typeId || types[0]?.id || '');
  const [name, setName] = useState(template?.name || '');
  const [contentHtml, setContentHtml] = useState(template?.contentHtml || '');

  const isEdit = !!template;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      alert('템플릿명을 입력해주세요.');
      return;
    }

    if (!isEdit && !typeId) {
      alert('면담 타입을 선택해주세요.');
      return;
    }

    try {
      await onSave({
        ...(isEdit ? {} : { typeId }),
        name: name.trim(),
        contentHtml,
      });
    } catch (err: any) {
      alert(err.message || '저장에 실패했습니다.');
    }
  };

  return (
    <>
      {/* Backdrop */}
      <ModalOverlay
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 998,
        }}
        onClose={onClose}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          width: '100%',
          maxWidth: '640px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 999,
        }}
      >
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--color-gray-200)',
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
            {isEdit ? '면담 템플릿 수정' : '면담 템플릿 추가'}
          </h2>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '24px',
          }}
        >
          {!isEdit && (
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>
                면담 타입 <span style={{ color: 'var(--color-red-500)' }}>*</span>
              </label>
              <select
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
                style={inputStyle}
                disabled={isSaving}
              >
                {types
                  .filter((t) => t.isActive)
                  .map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>
              템플릿명 <span style={{ color: 'var(--color-red-500)' }}>*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 정기면담 기본 템플릿"
              style={inputStyle}
              disabled={isSaving}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>템플릿 본문 (HTML)</label>
            <textarea
              value={contentHtml}
              onChange={(e) => setContentHtml(e.target.value)}
              placeholder="<p>면담 내용을 입력하세요...</p>"
              rows={10}
              style={{
                ...inputStyle,
                resize: 'none',
                fontFamily: 'monospace',
                fontSize: '13px',
              }}
              disabled={isSaving}
            />
            <p style={{
              margin: '6px 0 0 0',
              fontSize: '12px',
              color: 'var(--color-gray-500)',
            }}>
              HTML 태그를 사용하여 본문 형식을 지정할 수 있습니다.
            </p>
          </div>
        </form>

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--color-gray-200)',
          display: 'flex',
          gap: '12px',
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--color-gray-700)',
              background: 'var(--color-gray-100)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
            disabled={isSaving}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            style={{
              flex: 1,
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'white',
              background: 'var(--color-blue-600)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              opacity: isSaving ? 0.5 : 1,
            }}
            disabled={isSaving}
          >
            {isSaving ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                저장 중...
              </span>
            ) : (
              '저장'
            )}
          </button>
        </div>
      </div>
    </>
  );
}

// ============================================
// 공용 스타일
// ============================================

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: 'var(--color-gray-700)',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

export default InterviewManagementPage;
